import { useEffect, useRef, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { FileSignature, Save, CheckCircle2, AlertTriangle, Upload, X, ImageIcon } from 'lucide-react';

/**
 * Letter signature settings card — visible on Profile for HR/Admin users only.
 * Saved details auto-appear on every Offer / Termination / Relieving / Experience letter
 * they issue (rendered by the backend via _signer_block_for()).
 */
export default function LetterSignatureSection() {
  const { user } = useAuth();
  const roles = user?.roles || [user?.role].filter(Boolean);
  const canManage = roles.some(r => ['admin', 'hr'].includes(r));

  const [form, setForm] = useState({
    signer_name: '', signer_designation: '', contact_email: '',
    contact_phone: '', contact_address: '', footer_note: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Signature image state
  const [sigMeta, setSigMeta] = useState(null);
  const [sigPreview, setSigPreview] = useState(null);
  const [sigBusy, setSigBusy] = useState(false);
  const sigFileRef = useRef(null);

  const loadSig = async () => {
    try {
      const { data } = await api.get('/letter-settings');
      setSigMeta(data.signature_image || null);
      if (data.signature_image?.exists) {
        const resp = await api.get('/letter-settings/signature/preview', { responseType: 'blob' });
        const url = URL.createObjectURL(resp.data);
        setSigPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      } else { setSigPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; }); }
    } catch (e) { /* silent */ }
  };

  const uploadSig = async (f) => {
    if (!f) return;
    const ok = ['image/png', 'image/jpeg', 'image/svg+xml'].includes(f.type) ||
      ['png', 'jpg', 'jpeg', 'svg'].includes(f.name?.toLowerCase().split('.').pop());
    if (!ok) return toast.error('Choose a PNG, JPG, JPEG or SVG.');
    if (f.size > 500_000) return toast.error('Image must be 500 KB or smaller.');
    setSigBusy(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      await api.post('/letter-settings/signature', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Signature image saved — appears on all letters you issue.');
      await loadSig();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSigBusy(false); if (sigFileRef.current) sigFileRef.current.value = ''; }
  };

  const removeSig = async () => {
    if (!window.confirm('Remove your signature image? Letter PDFs you issue will show only a signature line.')) return;
    setSigBusy(true);
    try {
      await api.delete('/letter-settings/signature');
      toast.success('Signature image removed.');
      await loadSig();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSigBusy(false); }
  };

  /* eslint-disable */
  useEffect(() => {
    if (!canManage) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get('/letter-settings');
        setForm({
          signer_name: data.signer_name || '',
          signer_designation: data.signer_designation || '',
          contact_email: data.contact_email || '',
          contact_phone: data.contact_phone || '',
          contact_address: data.contact_address || '',
          footer_note: data.footer_note || '',
        });
        // If updated_at is null, the user has never set this — show "incomplete" state.
        setSaved(!!data.updated_at);
        setSigMeta(data.signature_image || null);
        if (data.signature_image?.exists) {
          const resp = await api.get('/letter-settings/signature/preview', { responseType: 'blob' });
          const url = URL.createObjectURL(resp.data);
          setSigPreview(url);
        }
      } catch (e) { console.debug('[letter-settings load]', e); }
      finally { setLoading(false); }
    })();
    return () => { setSigPreview((p) => { if (p) URL.revokeObjectURL(p); return null; }); };
  }, [canManage]);
  /* eslint-enable */

  if (!canManage) return null;

  const save = async () => {
    if (!form.signer_name.trim()) { toast.error('Your name is required'); return; }
    setSaving(true);
    try {
      await api.put('/letter-settings', form);
      toast.success('Letter signature saved — appears on all letters you issue');
      setSaved(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="letter-signature-section">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 text-[#3D4A8E] flex items-center justify-center flex-shrink-0">
          <FileSignature className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Document signature</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">Your letter signature</h2>
          <p className="text-sm text-slate-500 mt-1">
            These details auto-appear in the signature block of every <b>Offer</b>, <b>Termination</b>, <b>Relieving</b> and <b>Experience</b> letter you issue.
          </p>
        </div>
        {saved ? (
          <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1 flex-shrink-0">
            <CheckCircle2 className="w-3 h-3" /> Saved
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-700 inline-flex items-center gap-1 flex-shrink-0">
            <AlertTriangle className="w-3 h-3" /> Not configured
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 py-4">Loading…</div>
      ) : (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Your name *</Label>
              <Input data-testid="ls-name" value={form.signer_name}
                     onChange={(e) => setForm({ ...form, signer_name: e.target.value })}
                     placeholder="e.g. Priya Sharma" className="rounded-xl mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Designation</Label>
              <Input data-testid="ls-designation" value={form.signer_designation}
                     onChange={(e) => setForm({ ...form, signer_designation: e.target.value })}
                     placeholder="Human Resources" className="rounded-xl mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Contact email</Label>
              <Input data-testid="ls-email" type="email" value={form.contact_email}
                     onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                     placeholder="hr@lanvaus.com" className="rounded-xl mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Contact phone</Label>
              <Input data-testid="ls-phone" value={form.contact_phone}
                     onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                     placeholder="+91 …" className="rounded-xl mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Office address</Label>
            <Textarea data-testid="ls-address" rows={2} value={form.contact_address}
                      onChange={(e) => setForm({ ...form, contact_address: e.target.value })}
                      placeholder="Address that appears under your signature" className="rounded-xl mt-1" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Footer note (optional)</Label>
            <Textarea data-testid="ls-footer" rows={2} value={form.footer_note}
                      onChange={(e) => setForm({ ...form, footer_note: e.target.value })}
                      placeholder="e.g. 'This is a computer-generated document and does not require a physical signature.'" className="rounded-xl mt-1" />
          </div>

          {/* Signature image — rendered above the typed name in every letter you issue */}
          <div className="border-t border-slate-100 pt-4" data-testid="ls-signature-image-section">
            <div className="flex items-center gap-2 mb-2">
              <ImageIcon className="w-4 h-4 text-slate-500" />
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600 mb-0">Signature image</Label>
              {sigMeta?.exists && (
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Uploaded
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mb-2">
              Optional. When uploaded, the image appears <b>above your typed name</b> in every letter PDF you issue.
              <b className="text-slate-700"> Transparent PNG recommended.</b> Rendered at max 180&nbsp;×&nbsp;80&nbsp;px.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <div className="w-48 h-24 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 flex items-center justify-center overflow-hidden flex-shrink-0"
                   data-testid="ls-signature-preview">
                {sigPreview ? (
                  <img src={sigPreview} alt="Signature" className="max-w-full max-h-full object-contain" />
                ) : (
                  <div className="text-xs text-slate-400 text-center px-2">No image yet</div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                {sigMeta?.exists ? (
                  <div className="text-xs text-slate-500 space-y-0.5">
                    <div><b className="text-slate-700">File:</b> {sigMeta.filename}</div>
                    <div><b className="text-slate-700">Size:</b> {(sigMeta.size / 1024).toFixed(1)} KB · {sigMeta.content_type}</div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">PNG / JPG / JPEG / SVG · max 500 KB.</p>
                )}
                <div className="flex flex-wrap gap-2">
                  <input ref={sigFileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,.png,.jpg,.jpeg,.svg"
                         className="hidden" data-testid="ls-sig-file-input"
                         onChange={(e) => uploadSig(e.target.files?.[0])} />
                  <Button onClick={() => sigFileRef.current?.click()} disabled={sigBusy} data-testid="ls-sig-upload"
                          variant="outline" className="rounded-full">
                    <Upload className="w-3.5 h-3.5 mr-1.5" /> {sigMeta?.exists ? 'Replace' : 'Upload signature'}
                  </Button>
                  {sigMeta?.exists && (
                    <Button variant="outline" onClick={removeSig} disabled={sigBusy} data-testid="ls-sig-remove"
                            className="rounded-full border-red-200 text-red-700 hover:bg-red-50">
                      <X className="w-3.5 h-3.5 mr-1.5" /> Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900">
            <b>Where does this appear?</b> On every PDF letter you generate from the Approvals page, the Documents tab of any employee, or the offboarding completion flow. Each HR/Admin has their own signature — when someone else issues a letter, their saved details are used instead.
          </div>
          <div className="flex justify-end">
            <Button data-testid="ls-save" onClick={save} disabled={saving || !form.signer_name.trim()}
                    className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save signature'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
